import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import MgcWrapper from '../MgcWrapper';
import ScopeWrapper from '../ScopeWrapper';
import StrategyOverviewWrapper from '../StrategyOverviewWrapper';
import AttachmentComp from '../AttachmentComp';
import SpendChartWrapper from '../SpendChart/SpendChartWrapper';
import {
  CHART_ALL_COLORS,
  CHART_All_DATASETS,
  CHART_COLORS,
  CHART_DATASETS,
  RENAME_MAP,
  RENAME_STRATEGY_MAP
} from '../../shared/AppConstants';
import SupplierTabsCard from '../Supplier/SupplierTabsCard';
import { useSelector } from 'react-redux';
import CommentComp from '../CommentComp';

export default function StrategyInfoTab({
  strategicSupplierDataGridApiRef,
  nonStrategicSupplierDataGridApiRef,
  masterMGCJson,
  setParentMgcData,
  selectedMgc,
  selectedMgcList,
  selectedMgcAfterEdit,
  resetSelectedMgcAfterEdit,
  register,
  commonProps,
  duplicateStrategyList,
  okToPublish,
  masterScopeJson,
  hasUSScope,
  strategyNumber,
  owners,
  approvers,
  ownerObj,
  userInfoMap,
  selectedOverviewViewList,
  setSelectedOverviewViewList,
  readOnly,
  isAdmin,
  isSBM,
  isCommMgr,
  info,
  allMGCSpend,
  suppliersMessage,
  supplierScopeOverview,
  collapseExpandedSuppliers,
  setSuppliersDirty,
  setParentSupplierData,
  setSuppliersMessage,
  setParentNonStrategicToPersist,
  setParentNonStrategicSupplierData,
  setCollapseExpandedSuppliers,
  getSpendErrorMsg,
  strategyInformation,
  setIsStrategyOverviewDirty
}) {
  const isEdit = useSelector((state) => state.strategy.isEdit);
  const spendGraphData = useSelector((state) => state.strategy.spendGraphData);

  return (
    <>
      {/* Main info + charts row */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {/* Left column: MGC, Scope, Overview, Comments, Attachments */}
        <Grid
          size={{ xs: 12, lg: isEdit ? 6 : 12 }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Card>
            <MgcWrapper
              strategyInformation={strategyInformation}
              masterMGCJson={masterMGCJson}
              setParentMgcData={setParentMgcData}
              selectedMgc={selectedMgc}
              selectedMGCList={selectedMgcList}
              isEditable={isEdit}
              selectedMgcAfterEdit={selectedMgcAfterEdit}
              resetSelectedMgcAfterEdit={resetSelectedMgcAfterEdit}
              label={' MGC (Material Group Code)'}
              register={register}
              commonProps={commonProps}
              duplicateStrategyState={
                duplicateStrategyList && duplicateStrategyList.length > 0
                  ? okToPublish
                  : null
              }
            />
          </Card>

          <ScopeWrapper
            strategyInformation={strategyInformation}
            isEdit={isEdit}
            hasUSScope={hasUSScope}
            masterScopeJson={masterScopeJson}
            register={register}
            commonProps={commonProps}
            strategyNumber={strategyNumber}
            okToPublish={okToPublish}
            duplicateStrategyState={
              duplicateStrategyList && duplicateStrategyList.length > 0
                ? okToPublish
                : null
            }
          />

          {owners && approvers && (
            <StrategyOverviewWrapper
              ownerObj={ownerObj}
              ownerDefaultValue={(isAdmin || isSBM) && ownerObj}
              masterOwnerUserList={owners ? owners : []}
              masterApproverUserList={approvers ? approvers : []}
              approverDefaultValue={(isAdmin || isCommMgr) && ownerObj}
              readOnly={readOnly}
              strategyInformation={strategyInformation}
              commonProps={commonProps}
              userInfoMap={userInfoMap}
              selectedOverviewViewList={selectedOverviewViewList}
              setSelectedOverviewViewList={setSelectedOverviewViewList}
              forCreate={strategyNumber === 0}
              setIsStrategyOverviewDirty={setIsStrategyOverviewDirty}
            />
          )}

          {isEdit && (
            <>
              <CommentComp
                userInfoMap={userInfoMap}
                isEdit={isEdit}
                commonProps={commonProps}
              />
              <AttachmentComp
                key={strategyInformation?.attachments}
                attachments={strategyInformation?.attachments}
                userInfoMap={userInfoMap}
                isEdit={isEdit}
                commonProps={commonProps}
              />
            </>
          )}
        </Grid>

        {/* Spend charts — edit mode only */}
        {isEdit && strategyInformation && (
          <>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Card>
                <SpendChartWrapper
                  data={spendGraphData}
                  chart_colors={CHART_COLORS}
                  chart_datasets={CHART_DATASETS}
                  rename_map={RENAME_MAP}
                  userId={info.userId}
                  title="Strategy Spend"
                  subTitle="Invoiced & Forecasted spend for this strategy"
                  noSpendErrMsg={getSpendErrorMsg(strategyInformation)}
                  noSpendMsg="No spend for this strategy"
                />
              </Card>
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Card>
                <SpendChartWrapper
                  data={allMGCSpend}
                  chart_colors={CHART_ALL_COLORS}
                  chart_datasets={CHART_All_DATASETS}
                  rename_map={RENAME_STRATEGY_MAP}
                  userId={info.userId}
                  title="All MGC Spend"
                  subTitle="All John Deere spend in these MGC's"
                  noSpendErrMsg={getSpendErrorMsg(strategyInformation, true)}
                  noSpendMsg="No spend for MGC & Unit combination"
                />
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Supplier table — edit mode only */}
      {isEdit && (
        <Card
          sx={(theme) => ({
            mt: theme.fuel6Tokens.JddcSpacingMedium,
            p: theme.fuel6Tokens.JddcSpacingMedium
          })}
        >
          {suppliersMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>{suppliersMessage}</AlertTitle>
            </Alert>
          )}
          <SupplierTabsCard
            key={strategyInformation}
            strategicSupplierDataGridApiRef={strategicSupplierDataGridApiRef}
            nonStrategicSupplierDataGridApiRef={
              nonStrategicSupplierDataGridApiRef
            }
            strategyInformation={strategyInformation}
            supplierScopeOverview={supplierScopeOverview}
            commonProps={commonProps}
            showSensitiveInfo={true}
            isEdit={isEdit}
            userInfoMap={userInfoMap}
            collapseExpandedSuppliers={collapseExpandedSuppliers}
            setSuppliersDirty={setSuppliersDirty}
            setSuppliersData={setParentSupplierData}
            setSuppliersMessage={setSuppliersMessage}
            setNonStrategicSuppliersList={setParentNonStrategicToPersist}
            setNonStrategicSuppliersData={setParentNonStrategicSupplierData}
            setCollapseExpandedSuppliers={setCollapseExpandedSuppliers}
          />
        </Card>
      )}
    </>
  );
}
